import React from 'react';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import List from '@material-ui/core/List';
import {makeStyles} from '@material-ui/core/styles';
import HomeIcon from '@material-ui/icons/Home';
import ListIcon from '@material-ui/icons/List';
import AddIcon from '@material-ui/icons/Add';
import ContactsIcon from '@material-ui/icons/Contacts';
import AssignmentIcon from '@material-ui/icons/Assignment';
import {NavLink, useLocation} from "react-router-dom";
import AccountBoxIcon from '@material-ui/icons/AccountBox';
import SettingsApplicationsIcon from "@material-ui/icons/SettingsApplications";
import AllInboxIcon from "@material-ui/icons/AllInbox";
import StorageIcon from "@material-ui/icons/Storage";
import TextFieldsIcon from '@material-ui/icons/TextFields';
import GavelIcon from '@material-ui/icons/Gavel';
import PersonPinIcon from '@material-ui/icons/PersonPin';
import BarChartIcon from '@material-ui/icons/BarChart';
import LocalHospitalIcon from '@material-ui/icons/LocalHospital';
import AccountBalanceIcon from '@material-ui/icons/AccountBalance';
import HistoryIcon from '@material-ui/icons/History';
import PersonAddIcon from '@material-ui/icons/PersonAdd';
import BusinessIcon from '@material-ui/icons/Business';
import DeleteSweepIcon from '@material-ui/icons/DeleteSweep';
import {getAccess, hasRouteClaim} from "../../helpers/permissions";

const BRAND_DEEP = '#2c4767';
const BRAND_DEEP_DARK = '#1e3553';
const BRAND_GOLD = '#b08d57';
const INK = '#1a2733';
const INK_SOFT = '#5a6675';

const useNavStyles = makeStyles((theme) => ({
    brand: {
        padding: theme.spacing(2.5, 2),
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5),
        background: `linear-gradient(135deg, ${BRAND_DEEP} 0%, ${BRAND_DEEP_DARK} 100%)`,
        color: '#ffffff',
        position: 'relative',
        '&::after': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: 2,
            background: `linear-gradient(90deg, transparent 0%, ${BRAND_GOLD} 50%, transparent 100%)`,
        },
    },
    brandMark: {
        width: 40,
        height: 40,
        borderRadius: 10,
        background: 'rgba(176, 141, 87, 0.18)',
        border: `1.5px solid ${BRAND_GOLD}`,
        color: '#f0d9a8',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 700,
        fontSize: '1rem',
        letterSpacing: 1,
        flexShrink: 0,
    },
    brandText: {
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
    },
    brandName: {
        fontWeight: 700,
        fontSize: '0.92rem',
        letterSpacing: 1.5,
        textTransform: 'uppercase',
        lineHeight: 1.2,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    brandTag: {
        fontSize: '0.7rem',
        color: '#e8d9b8',
        letterSpacing: 1.5,
        textTransform: 'uppercase',
        marginTop: 2,
        opacity: 0.9,
    },
    section: {
        padding: theme.spacing(1, 0),
    },
    subheader: {
        padding: theme.spacing(2, 2, 0.75, 2),
        fontSize: '0.68rem',
        fontWeight: 700,
        letterSpacing: 1.4,
        textTransform: 'uppercase',
        color: INK_SOFT,
    },
    sectionDivider: {
        height: 1,
        margin: theme.spacing(0.5, 2),
        background: 'rgba(176, 141, 87, 0.18)',
        border: 0,
    },
    navLink: {
        textDecoration: 'none',
        color: 'inherit',
        display: 'block',
    },
    navItem: {
        margin: theme.spacing(0.25, 1),
        borderRadius: 10,
        padding: theme.spacing(1, 1.25),
        color: INK,
        position: 'relative',
        transition: 'background 150ms ease, color 150ms ease',
        '&:hover': {
            background: 'rgba(44, 71, 103, 0.06)',
            color: BRAND_DEEP,
        },
        '& .MuiListItemIcon-root': {
            minWidth: 36,
            color: INK_SOFT,
            transition: 'color 150ms ease',
        },
        '&:hover .MuiListItemIcon-root': {
            color: BRAND_DEEP,
        },
        '& .MuiListItemText-primary': {
            fontWeight: 500,
            fontSize: '0.92rem',
        },
    },
    navItemActive: {
        background: `linear-gradient(135deg, rgba(44, 71, 103, 0.10) 0%, rgba(176, 141, 87, 0.12) 100%)`,
        color: BRAND_DEEP,
        boxShadow: 'inset 3px 0 0 ' + BRAND_GOLD,
        '&:hover': {
            background: `linear-gradient(135deg, rgba(44, 71, 103, 0.14) 0%, rgba(176, 141, 87, 0.18) 100%)`,
        },
        '& .MuiListItemIcon-root': {
            color: BRAND_DEEP,
        },
        '& .MuiListItemText-primary': {
            fontWeight: 700,
        },
    },
    footer: {
        marginTop: 'auto',
        padding: theme.spacing(2),
        borderTop: '1px solid rgba(176, 141, 87, 0.18)',
        background: 'rgba(176, 141, 87, 0.05)',
        fontSize: '0.72rem',
        color: INK_SOFT,
        textAlign: 'center',
        letterSpacing: 0.4,
    },
}));

const mainItems = [
    {to: '/domov', label: 'Domov', icon: HomeIcon},
    {to: '/novy-pohreb', label: 'Vytvoriť pohreb', icon: AddIcon},
    {to: '/zoznam', label: 'Zoznam pohrebov', icon: ListIcon},
    {to: '/ulohy', label: 'Úlohy', icon: AssignmentIcon},
];

const helperItems = [
    {to: '/kontakty', label: 'Kontakty', icon: ContactsIcon},
    {to: '/statistiky', label: 'Štatistiky', icon: BarChartIcon},
    {to: '/generovanie_evidencie', label: 'Export evidencie', icon: StorageIcon},
    {to: '/sluzby_obhliadajucich_lekarov', label: 'Služby lekárov', icon: LocalHospitalIcon},
    {to: '/matrika', label: 'Matrika', icon: AccountBalanceIcon},
];

const settingsItems = [
    {to: '/profil', label: 'Profil', icon: AccountBoxIcon},
    {to: '/organizacia', label: 'Organizácia', icon: BusinessIcon},
    {to: '/kos', label: 'Kôš', icon: DeleteSweepIcon, ownerOnly: true},
    {to: '/definovane_texty', label: 'Preddefinované texty', icon: TextFieldsIcon},
    {to: '/sluzby', label: 'Tvorba služieb', icon: SettingsApplicationsIcon},
    {to: '/produkty', label: 'Tvorba produktov', icon: AllInboxIcon},
    {to: '/definovane_ulohy', label: 'Tvorba úloh', icon: AssignmentIcon},
];

const legalItems = [
    {to: '/podmienky', label: 'Obchodné podmienky', icon: GavelIcon},
    {to: '/gdpr', label: 'GDPR', icon: PersonPinIcon},
];

const adminItems = [
    {to: '/kontakty', label: 'Kontakty', icon: ContactsIcon},
    {to: '/matrika', label: 'Matrika', icon: AccountBalanceIcon},
    {to: '/logs', label: 'Logy', icon: HistoryIcon},
    {to: '/register', label: 'Registrácia', icon: PersonAddIcon},
];

function NavItem({to, label, icon: Icon, classes, onClick}) {
    const location = useLocation();
    const isActive = location.pathname === to || location.pathname.startsWith(to + '/');
    return (
        <NavLink to={to} className={classes.navLink} onClick={onClick}>
            <ListItem
                button
                disableGutters
                className={`${classes.navItem} ${isActive ? classes.navItemActive : ''}`}
            >
                <ListItemIcon>
                    <Icon fontSize="small"/>
                </ListItemIcon>
                <ListItemText primary={label}/>
            </ListItem>
        </NavLink>
    );
}

function NavSection({title, items, classes, onItemClick}) {
    return (
        <div className={classes.section}>
            <div className={classes.subheader}>{title}</div>
            <List disablePadding>
                {items.map((item) => (
                    <NavItem
                        key={item.to}
                        to={item.to}
                        label={item.label}
                        icon={item.icon}
                        classes={classes}
                        onClick={onItemClick}
                    />
                ))}
            </List>
        </div>
    );
}

export default function NavList({onItemClick, showFooter = true, showBrand = true, topSpacerHeight = 0, showAdminBoard = false}) {
    const classes = useNavStyles();
    const access = getAccess();
    const visibleMainItems = mainItems.filter((item) => (!item.ownerOnly || access.isOwner) && hasRouteClaim(item.to) && (item.to !== '/novy-pohreb' || access.write));
    const visibleHelperItems = helperItems.filter((item) => hasRouteClaim(item.to));
    const visibleSettingsItems = settingsItems.filter((item) => {
        if (item.ownerOnly || item.to === '/profil' || item.to === '/organizacia') return access.isOwner;
        return hasRouteClaim(item.to);
    });

    return (
        <>
            {topSpacerHeight > 0 && (
                <div style={{minHeight: topSpacerHeight, flexShrink: 0}}/>
            )}
            {showBrand && (
                <div className={classes.brand}>
                    <div className={classes.brandMark}>MP</div>
                    <div className={classes.brandText}>
                        <span className={classes.brandName}>Moderné pohrebníctvo</span>
                        <span className={classes.brandTag}>Pohrebná aplikácia</span>
                    </div>
                </div>
            )}

            {showAdminBoard ? (
                <>
                    <NavSection title="Administrácia" items={adminItems} classes={classes} onItemClick={onItemClick}/>
                    <div className={classes.sectionDivider}/>
                    <NavSection title="Právne dokumenty" items={legalItems} classes={classes} onItemClick={onItemClick}/>
                </>
            ) : (
                <>
                    <NavSection title="Hlavné menu" items={visibleMainItems} classes={classes} onItemClick={onItemClick}/>
                    {visibleHelperItems.length > 0 && <div className={classes.sectionDivider}/>}
                    {visibleHelperItems.length > 0 && <NavSection title="Pomôcky a nástroje" items={visibleHelperItems} classes={classes} onItemClick={onItemClick}/>}
                    {visibleSettingsItems.length > 0 && <div className={classes.sectionDivider}/>}
                    {visibleSettingsItems.length > 0 && <NavSection title="Nastavenia" items={visibleSettingsItems} classes={classes} onItemClick={onItemClick}/>}
                    <div className={classes.sectionDivider}/>
                    <NavSection title="Právne dokumenty" items={legalItems} classes={classes} onItemClick={onItemClick}/>
                </>
            )}

            {showFooter && (
                <div className={classes.footer}>
                    © {new Date().getFullYear()} Moderné pohrebníctvo
                </div>
            )}
        </>
    );
}
